<script setup lang="ts">
import { getRegulatorList as getRegulatorDisclosureList, type RegulatorListItem } from '~/api/disclosure'
import {
  getRegulatorDetail,
  getRegulatorCodeByEnShortName,
  type RegulatorDetailComplaintItem,
  type RegulatorDetailDisclosure,
} from '~/api/regulator-list'
import ArrowDownIcon from '~/assets/icon/arrow-down.svg'
import CloseIcon from '~/assets/icon/close-2.svg'
import RegulationMoreIcon from '~/assets/icon/regulation-more.svg'
import ShareOIcon from '~/assets/icon/share-o.svg'
import { normalizeDisclosureSlug } from '~/utils/disclosure-route'
import { resolveRegulatorDetailLayout } from '~/utils/regulator-detail-layout'

definePageMeta({
  validate: (route) => {
    const regulatorSlug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug
    return Boolean(String(regulatorSlug || '').trim())
  },
})

const { localePath, t } = useAppI18n()
const { isRtl } = useRtl()
const route = useRoute()
const { setBreadcrumb } = useLsBreadcrumb()
const { lock: lockPageScroll, unlock: unlockPageScroll } = useStickyDialogScrollLock()

const { $toast } = useNuxtApp()

const isRegulationPopupVisible = ref(false)
const descriptionRef = ref<HTMLElement | null>(null)
const isDescriptionExpanded = ref(false)
const shouldShowDescriptionExpand = ref(false)
const descriptionPreview = ref('')
const regulationPopupBodyRef = ref<HTMLElement | null>(null)
const regulationNoteContentRefs = new Map<number, HTMLElement>()
const expandedRegulationNoteIndexes = ref(new Set<number>())
const expandableRegulationNoteIndexes = ref(new Set<number>())
const regulationPopupScrollMetrics = ref({ scrollTop: 0, scrollHeight: 0, clientHeight: 0 })

const REGULATION_POPUP_SCROLLBAR_THUMB_HEIGHT = 320

/** 当前监管机构 slug，兼容 Nuxt 动态路由数组入参。 */
const regulatorSlug = computed(() => normalizeRouteValue(route.params.slug))

/** 详情接口直接使用路由中的 urlSlug 查询监管机构。 */
const regulatorCode = computed(() => regulatorSlug.value)

/** 统一生成监管详情 404，覆盖参数校验后的接口异常和客户端切换异常。 */
const createRegulatorNotFoundError = () =>
  createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
    fatal: true,
  })

const { data: regulatorDetailData, error: regulatorDetailError } = await useAsyncData(
  () => `regulator-detail-${regulatorCode.value}`,
  async () => {
    const detail = await getRegulatorDetail(regulatorCode.value)
    if (!detail) {
      throw createRegulatorNotFoundError()
    }

    return detail
  },
  {
    watch: [regulatorCode],
  },
)

if (regulatorDetailError.value || !regulatorDetailData.value) {
  throw createRegulatorNotFoundError()
}

watch(regulatorDetailError, (error) => {
  if (error) {
    showError(createRegulatorNotFoundError())
  }
})

const regulatorDetail = computed(() => regulatorDetailData.value)
const regulatorLayout = computed(() => resolveRegulatorDetailLayout(regulatorDetail.value))
const { data: disclosureRegulatorCodeData } = await useAsyncData(
  () => `regulator-detail-disclosure-code-${regulatorCode.value}`,
  () => getRegulatorCodeByEnShortName(regulatorCode.value),
  {
    default: () => '',
    watch: [regulatorCode],
  },
)
const disclosureRegulatorCode = computed(() => disclosureRegulatorCodeData.value || '')
const { data: disclosureRouteData } = await useAsyncData(
  () => `regulator-detail-disclosure-routes-${disclosureRegulatorCode.value}`,
  () =>
    getRegulatorDisclosureList({
      cate: 'all',
      regulatorCodes: disclosureRegulatorCode.value,
      pageindex: 1,
      pagesize: 20,
    }),
  {
    default: () => ({ items: [], areaItems: [] }),
    watch: [disclosureRegulatorCode],
  },
)
/** 从交易所牌照入口传入的来源交易所，用于还原详情页面包屑。 */
const sourceExchangeSlug = computed(() => normalizeRouteValue(route.query.exchange))
const sourceExchangeName = computed(() => normalizeRouteValue(route.query.exchangeName))
const regulatorTitle = computed(
  () =>
    regulatorDetail.value?.regulatorName ||
    regulatorDetail.value?.regulatorShortName ||
    regulatorDetail.value?.regulatorCode ||
    t('82502'),
)
const regulatorLogo = computed(
  () => regulatorDetail.value?.regulatorImage || regulatorDetail.value?.regulatorIcon || '',
)
const regulatorEstablishedText = computed(() => regulatorDetail.value?.regulatorEstablishedText || '--')
const regulatorFoundationDate = computed(() => regulatorDetail.value?.foundationDate || '--')
const regulatorDescription = computed(() => regulatorDetail.value?.regulatorDescription || t('83714'))
const regulatorShortName = computed(() => regulatorDetail.value?.shortName || '--')
const regulatorCountry = computed(() => regulatorDetail.value?.regulatorCountry || '--')
const regulatorCountryIco = computed(() => regulatorDetail.value?.regulatorCountryIco || '')
const urlSlug = computed(() => regulatorDetail.value?.urlSlug || '')
/** 生成当前监管机构的监管披露页面链接。 */
const disclosuresPath = computed(() => {
  return localePath({
    name: 'regulatory-disclosure',
    params: {
      slug: urlSlug.value,
    },
  })
})
/** 将披露摘要 ID 映射到 SEO 详情路由需要的披露别名和交易所别名。 */
const disclosureRouteMap = computed(() => {
  const routeMap = new Map<string, { disclosureSlug: string; exchangeSlug: string; traderCode: string }>()

  disclosureRouteData.value.items.forEach((item: RegulatorListItem) => {
    const disclosureId = normalizeRouteValue(item.id || item.code || item.regId || item.RegId)
    const trader = Array.isArray(item.traders) ? item.traders[0] : undefined
    const disclosureSlug = normalizeRouteValue(item.alias)
    const exchangeAlias = normalizeRouteValue(trader?.alias)
    const exchangeSlug = normalizeDisclosureSlug(exchangeAlias, 'exchange')
    const traderCode = normalizeRouteValue(trader?.code || trader?.Code || trader?.traderCode || trader?.TraderCode)

    if (disclosureId && disclosureSlug && exchangeSlug) {
      routeMap.set(disclosureId, { disclosureSlug, exchangeSlug, traderCode })
    }
  })

  return routeMap
})
/** 披露区域首页仅展示前五条，完整列表由右侧入口承载。 */
const displayDisclosureItems = computed(() => regulatorDetail.value?.disclosureList.slice(0, 5) || [])
/** 仅当披露总数超过五条时展示通往完整披露列表的入口。 */
const hasMoreDisclosures = computed(() => {
  const detail = regulatorDetail.value
  return Math.max(detail?.disclosureCount || 0, detail?.disclosureList.length || 0) > 5
})
/** 会员区域首页仅展示前五条，完整列表由右侧入口承载。 */
const displayMemberItems = computed(() => regulatorDetail.value?.memberList.slice(0, 5) || [])
/** 仅当会员总数超过五条时展示通往完整会员列表的入口。 */
const hasMoreMembers = computed(() => {
  const detail = regulatorDetail.value
  if (detail?.memberCount === 1) {
    return false
  }

  return Math.max(detail?.memberCount || 0, detail?.memberList.length || 0) > 5
})
/** 黑名单区域首页仅展示前五条，完整列表由右侧入口承载。 */
const displayBlacklistItems = computed(() => regulatorDetail.value?.blacklistList.slice(0, 5) || [])
/** 仅当黑名单总数超过五条时展示通往完整黑名单列表的入口。 */
const hasMoreBlacklistEntries = computed(() => {
  const detail = regulatorDetail.value
  if (detail?.blacklistCount === 1) {
    return false
  }

  return Math.max(detail?.blacklistCount || 0, detail?.blacklistList.length || 0) > 5
})
/** 官网信息与访问入口统一使用详情接口 websites 的第一项。 */
const officialUrl = computed(() => regulatorDetail.value?.websites[0] || '')
const officialWebsiteText = computed(() => officialUrl.value || '--')

/** 计算可与省略号和箭头共同显示在第三行的最长简介文本。 */
const measureDescriptionOverflow = async () => {
  const description = descriptionRef.value
  if (!description || isDescriptionExpanded.value) {
    return
  }

  const fullDescription = regulatorDescription.value
  descriptionPreview.value = fullDescription
  shouldShowDescriptionExpand.value = false
  await nextTick()

  if (description.scrollHeight <= description.clientHeight) {
    return
  }

  shouldShowDescriptionExpand.value = true
  let minimumLength = 0
  let maximumLength = fullDescription.length

  // 二分裁剪，保留第三行中省略号和箭头之间的 12px 间距。
  while (minimumLength < maximumLength) {
    const middleLength = Math.ceil((minimumLength + maximumLength) / 2)
    descriptionPreview.value = fullDescription.slice(0, middleLength)
    await nextTick()

    if (description.scrollHeight > description.clientHeight) {
      maximumLength = middleLength - 1
    } else {
      minimumLength = middleLength
    }
  }

  descriptionPreview.value = fullDescription.slice(0, minimumLength)
}

/** 一次性展开完整监管机构简介，展开后不提供收起入口。 */
const expandDescription = () => {
  isDescriptionExpanded.value = true
}

/** 缓存合规要求正文节点，用于判断其是否超过 Figma 规定的六行展示高度。 */
const setRegulationNoteContentRef = (index: number, element: Element | null) => {
  if (element instanceof HTMLElement) {
    regulationNoteContentRefs.set(index, element)
  } else {
    regulationNoteContentRefs.delete(index)
  }
}

/** 测量每项合规要求正文，只有超过六行时才显示展开箭头。 */
const measureRegulationNoteOverflow = async () => {
  await nextTick()

  const expandableIndexes = new Set<number>()
  regulationNoteContentRefs.forEach((element, index) => {
    if (!expandedRegulationNoteIndexes.value.has(index) && element.scrollHeight > element.clientHeight) {
      expandableIndexes.add(index)
    }
  })
  expandableRegulationNoteIndexes.value = expandableIndexes
}

/** 展开指定合规要求的完整内容，展开后不再显示收起入口。 */
const expandRegulationNote = (index: number) => {
  expandedRegulationNoteIndexes.value = new Set([...expandedRegulationNoteIndexes.value, index])
  expandableRegulationNoteIndexes.value = new Set(
    [...expandableRegulationNoteIndexes.value].filter((expandableIndex) => expandableIndex !== index),
  )
  void nextTick(syncRegulationPopupScrollMetrics)
}

/** 同步内容区滚动状态，以固定高度的自定义滑块映射内容滚动位置。 */
const syncRegulationPopupScrollMetrics = () => {
  const content = regulationPopupBodyRef.value
  regulationPopupScrollMetrics.value = {
    scrollTop: content?.scrollTop || 0,
    scrollHeight: content?.scrollHeight || 0,
    clientHeight: content?.clientHeight || 0,
  }
}

const showRegulationPopupScrollbar = computed(() => {
  const { scrollHeight, clientHeight } = regulationPopupScrollMetrics.value
  return scrollHeight > clientHeight + 1
})

const regulationPopupScrollbarThumbStyle = computed(() => {
  const { scrollTop, scrollHeight, clientHeight } = regulationPopupScrollMetrics.value
  const thumbHeight = Math.min(REGULATION_POPUP_SCROLLBAR_THUMB_HEIGHT, clientHeight)
  const maxScrollTop = scrollHeight - clientHeight
  const maxThumbOffset = Math.max(clientHeight - thumbHeight, 0)
  const thumbOffset = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbOffset : 0

  return {
    height: `${thumbHeight}px`,
    transform: `translateY(${thumbOffset}px)`,
  }
})

/** 计数为零或一时不在模块标题中显示，减少无意义的信息噪音。 */
const displayCount = (count: number | undefined) => (Number(count) > 1 ? ` ${count}` : '')

/** RTL 语言将披露日期统一显示为 DD-MM-YYYY，其他语言维持接口原始格式。 */
const formatDisclosureDate = (value: string) => {
  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!matched || !isRtl.value) {
    return value || '--'
  }

  return `${matched[3]}-${matched[2]}-${matched[1]}`
}

/** 判断披露摘要是否包含可展示的交易所图标或名称。 */
const hasDisclosureBroker = (item: RegulatorDetailDisclosure) =>
  item.brokerList.some((broker) => broker.brokerIcon || broker.brokerShortName)

/**
 * 生成披露详情页本地化链接。
 * @param item 监管详情接口返回的披露摘要
 * @returns 包含披露、交易所和监管机构别名的详情页路径
 */
const getDisclosureDetailPath = (item: RegulatorDetailDisclosure) => {
  const routeData = disclosureRouteMap.value.get(item.disclosureId)
  if (!routeData) {
    return disclosuresPath.value
  }

  return localePath({
    name: 'disclosures-detail',
    params: {
      regulatorSlug: urlSlug.value,
      exchangeSlug: routeData.exchangeSlug,
      slug: routeData.disclosureSlug,
    },
  })
}

/** 生成交易所详情页本地化链接。 */
const getExchangeDetailPath = (exchangeSlug: string) =>
  localePath({
    name: 'exchange-detail',
    params: { slug: exchangeSlug },
  })

/** 复制投诉渠道内容；兼容 Clipboard API 不可用的浏览器。 */
const copyComplaintChannel = async (item: RegulatorDetailComplaintItem) => {
  const contactContent = item.contactContent.trim()
  if (!contactContent || typeof document === 'undefined') {
    return
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    await navigator.clipboard.writeText(contactContent)
  } else {
    const input = document.createElement('input')
    input.value = contactContent
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
  }

  $toast('success', t('82423'), {
    variant: 'figma-copy',
  })
}

onMounted(() => {
  void measureDescriptionOverflow()
  window.addEventListener('resize', measureDescriptionOverflow)
})

onBeforeUnmount(() => {
  unlockPageScroll()
  window.removeEventListener('resize', measureDescriptionOverflow)
})

/** 弹窗打开期间锁定页面背景，关闭后立即恢复背景滚动。 */
watch(isRegulationPopupVisible, (isVisible) => {
  if (isVisible) {
    lockPageScroll()
    return
  }

  unlockPageScroll()
})

watch(regulatorDescription, async () => {
  isDescriptionExpanded.value = false
  await nextTick()
  await measureDescriptionOverflow()
})

/**
 * 标准化动态路由参数。
 * @param value 原始路由值
 * @returns 可用于处理的 slug
 */
function normalizeRouteValue(value: unknown) {
  if (Array.isArray(value)) {
    return String(value[0] || '').trim()
  }

  return String(value || '').trim()
}

const breadcrumbItems = computed(() => [
  {
    label: t('82520'),
    route: '/',
  },
  ...(sourceExchangeSlug.value
    ? [
        {
          label: sourceExchangeName.value || sourceExchangeSlug.value,
          route: {
            name: 'exchange-detail',
            params: {
              slug: sourceExchangeSlug.value,
            },
          },
        },
      ]
    : [
        {
          label: t('84679'),
          route: {
            name: 'regulators',
          },
        },
      ]),
  {
    label: regulatorTitle.value,
  },
])

/** 显示由详情接口 regulationNotePopupList 提供的合规要求说明。 */
const openRegulationPopup = async () => {
  expandedRegulationNoteIndexes.value = new Set()
  expandableRegulationNoteIndexes.value = new Set()
  isRegulationPopupVisible.value = true
  await measureRegulationNoteOverflow()
  syncRegulationPopupScrollMetrics()
}

/** 关闭合规要求说明弹窗。 */
const closeRegulationPopup = () => {
  isRegulationPopupVisible.value = false
}

/** 打开监管机构官网，外链统一走 Nuxt navigateTo；接口无网址时不触发。 */
const openOfficialWebsite = () => {
  if (!officialUrl.value) {
    return
  }

  return navigateTo(officialUrl.value, { external: true })
}

const goMembers = () => {
  setBreadcrumb('disclosures-members', {
    label: regulatorTitle.value,
    route: route?.fullPath,
  })
}

useSeoMeta({
  title: () => t('84870', [regulatorTitle.value]),
  ogTitle: () => t('84870', [regulatorTitle.value]),
  description: () => t('84875', [regulatorTitle.value]),
  ogDescription: () => t('84875', [regulatorTitle.value]),
  keywords: () => [t('84671'), t('84650'), t('84625'), t('84640'), t('84658')].join(', '),
})
</script>

<template>
  <main class="min-w-[1200px] bg-[#ffffff] pt-[64px] text-[#2f3138]">
    <div class="mx-auto w-[1200px] pb-[160px]">
      <CommonBreadcrumb :items="breadcrumbItems" class="!py-[24px]" />

      <section class="flex items-start">
        <div
          class="flex h-[94px] w-[166px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-[#edf0f4] bg-[#ffffff]"
        >
          <CommonImage :src="regulatorLogo" :alt="regulatorTitle" class="size-full" object-fit="contain" />
        </div>

        <div class="ml-[30px] min-w-0 flex-1 rtl:mr-[30px] rtl:ml-0">
          <h1 class="text-[36px] leading-[44px] font-[600] tracking-[0] text-[#30323a]">
            {{ regulatorTitle }}
          </h1>
          <div
            class="my-[12px] inline-flex items-center rounded-[2px] bg-[#f8f8f8] px-[8px] py-[2px] text-[14px] leading-[20px] font-[400] text-[#292a2e]"
          >
            <CommonImage
              :src="regulatorCountryIco"
              :alt="regulatorCountry"
              class="mr-[5px] !h-[14px] !w-[22px] rtl:mr-0 rtl:ml-[5px]"
              object-fit="cover"
            />
            {{ regulatorEstablishedText }}
          </div>
          <p
            ref="descriptionRef"
            class="relative text-[16px] leading-[24px] font-[400] text-[#505258]"
            :class="{ 'max-h-[72px] overflow-hidden': !isDescriptionExpanded }"
          >
            {{ isDescriptionExpanded ? regulatorDescription : descriptionPreview }}
            <template v-if="shouldShowDescriptionExpand && !isDescriptionExpanded">...</template>
            <button
              v-if="shouldShowDescriptionExpand && !isDescriptionExpanded"
              type="button"
              class="inline-flex size-[20px] cursor-pointer text-[#7d818a] transition-transform hover:text-[#7d818a]"
              :class="'ml-[12px] align-text-bottom rtl:mr-[12px] rtl:ml-0'"
              aria-label="展开机构简介"
              @click="expandDescription"
            >
              <ArrowDownIcon class="size-[20px] [&_path]:fill-current" aria-hidden="true" />
            </button>
          </p>
        </div>
      </section>

      <section
        v-if="regulatorDetail?.regulationNotes.length"
        class="mt-[34px] grid grid-cols-5 gap-[16px]"
        :aria-label="t('84671')"
      >
        <article
          v-for="item in regulatorDetail.regulationNotes"
          :key="`${item.regulationBottomText}-${item.shortDescription}`"
          class="flex h-[114px] cursor-pointer flex-col items-center justify-center rounded-[6px] bg-[#f8f8f9] p-[12px] transition-colors hover:bg-[#f0f1f2]"
          role="button"
          tabindex="0"
          @click="openRegulationPopup"
          @keydown.enter="openRegulationPopup"
          @keydown.space.prevent="openRegulationPopup"
        >
          <div class="relative flex h-[28px] min-w-[28px] items-center justify-center">
            <CommonImage
              :src="item.regulationIcon"
              :alt="item.regulationBottomText"
              class="!h-[23px] !w-[21px]"
              object-fit="contain"
            />
            <CommonImage
              :src="item.regulationBottomRightIcon"
              :alt="item.regulationBottomText"
              class="absolute bottom-[0px] left-[15px] !size-[10px] rtl:right-[15px] rtl:right-auto"
              object-fit="contain"
            />
          </div>
          <h2 class="mt-[11px] line-clamp-2 text-center text-[14px] leading-[20px] font-[700] text-[#373a42]">
            {{ item.regulationBottomText || '--' }}
          </h2>
          <p
            v-if="item.shortDescription"
            class="mt-[3px] line-clamp-2 text-center text-[12px] leading-[18px] text-[#989ea8]"
          >
            {{ item.shortDescription }}
          </p>
        </article>
        <article
          class="flex h-[114px] cursor-pointer flex-col items-center justify-center gap-[8px] rounded-[8px] bg-[#f8f8f8] p-[12px] transition-colors hover:bg-[#f0f1f2]"
          role="button"
          tabindex="0"
          @click="openRegulationPopup"
          @keydown.enter="openRegulationPopup"
          @keydown.space.prevent="openRegulationPopup"
        >
          <div class="flex size-[40px] items-center justify-center">
            <RegulationMoreIcon class="size-[28px]" aria-hidden="true" />
          </div>
          <span class="truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]">{{ t('84570') }}</span>
        </article>
      </section>

      <section class="mt-[40px] grid grid-cols-[minmax(0,750px)_384px] gap-[64px]">
        <div class="min-w-0">
          <section v-if="regulatorLayout.hasDisclosureColumn">
            <div class="mb-[22px] flex items-center justify-between">
              <h2 class="text-[20px] leading-[28px] font-[700] text-[#34373d]">
                {{ t('82718') }}{{ displayCount(regulatorDetail?.disclosureCount) }}
              </h2>
              <NuxtLink
                v-if="hasMoreDisclosures"
                :to="disclosuresPath"
                target="_blank"
                class="cursor-pointer text-[24px] leading-[24px] text-[#9ca3af] transition-colors hover:text-[#292a2e]"
              >
                ›
              </NuxtLink>
            </div>

            <NuxtLink
              v-for="item in displayDisclosureItems"
              :key="item.disclosureId || item.titleText"
              :to="getDisclosureDetailPath(item)"
              target="_blank"
              class="block cursor-pointer border-b border-[#edf0f4] py-[20px] text-inherit no-underline transition-colors first:pt-0 hover:bg-[#f8f8f8]"
            >
              <h3
                :dir="isRtl ? 'rtl' : 'ltr'"
                class="[display:-webkit-box] overflow-hidden text-[17px] leading-[25px] font-[700] break-words text-[#3d4048] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] rtl:text-right"
              >
                {{ item.titleText }}
              </h3>
              <div class="mt-[12px] flex items-center">
                <div
                  v-if="hasDisclosureBroker(item)"
                  class="inline-flex h-[34px] items-center rounded-[4px] bg-[#f8f8f8] p-[8px] text-[12px] leading-[16px] font-[400] text-[#505258]"
                >
                  <div class="flex items-center">
                    <template v-for="broker in item.brokerList" :key="broker.brokerCode || broker.brokerIcon">
                      <CommonImage
                        :src="broker.brokerIcon"
                        :alt="broker.brokerShortName"
                        class="ml-[4px] !size-[18px] rounded-[4px] first:ml-0 rtl:mr-[-4px] rtl:ml-0 rtl:first:mr-0 rtl:first:ml-0"
                        object-fit="cover"
                      />
                    </template>
                  </div>
                  <span class="ml-[6px] rtl:mr-[6px] rtl:ml-0">
                    {{ item.brokerList[0]?.brokerShortName }}
                  </span>
                </div>
                <time
                  class="text-[12px] leading-[18px] text-[#8c8f97]"
                  :class="{ 'ml-[8px] rtl:mr-[8px] rtl:ml-0': hasDisclosureBroker(item) }"
                >
                  {{ formatDisclosureDate(item.time) }}
                </time>
              </div>
            </NuxtLink>
          </section>

          <section
            v-if="regulatorLayout.hasMemberColumn && regulatorDetail?.memberList.length"
            :class="{ 'mt-[48px]': regulatorLayout.hasDisclosureColumn }"
          >
            <div class="mb-[20px] flex items-center justify-between">
              <h2 class="text-[20px] leading-[28px] font-[700] text-[#34373d]">
                {{ t('84625') }}{{ displayCount(regulatorDetail?.memberCount) }}
              </h2>
              <NuxtLink
                v-if="hasMoreMembers"
                :to="localePath({ name: 'disclosures-members' })"
                @click="goMembers"
                target="_blank"
                class="cursor-pointer text-[24px] leading-[24px] text-[#9ca3af] transition-colors hover:text-[#292a2e]"
              >
                ›
              </NuxtLink>
            </div>
            <div
              class="grid grid-cols-[280px_minmax(0,1fr)_80px] border-b border-[#edf0f4] pb-[10px] text-[13px] leading-[18px] text-[#a2a7b0]"
            >
              <span>{{ t('84676') }}</span>
              <span class="px-[12px]">{{ t('84340') }}</span>
              <span class="text-right">{{ t('82495') }}</span>
            </div>
            <NuxtLink
              v-for="item in displayMemberItems"
              :key="item.brokerCode || item.brokerShortName"
              :to="getExchangeDetailPath(item.alias)"
              target="_blank"
              class="grid min-h-[72px] cursor-pointer grid-cols-[280px_minmax(0,1fr)_80px] items-center border-b border-[#edf0f4] text-inherit no-underline transition-colors hover:bg-[#f8f8f8]"
            >
              <div class="flex min-w-0 items-center pr-[12px]">
                <div
                  class="flex size-[44px] shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-[#f5f7fb] text-[18px] leading-[24px] font-[800] text-[#142f83]"
                >
                  <CommonImage
                    :src="item.brokerIcon"
                    :alt="item.brokerShortName"
                    class="size-full"
                    object-fit="cover"
                  />
                </div>
                <div class="ml-[13px] min-w-0 rtl:mr-[13px] rtl:ml-0">
                  <h3
                    :dir="isRtl ? 'rtl' : 'ltr'"
                    class="[display:-webkit-box] overflow-hidden text-[16px] leading-[22px] font-[700] break-words text-[#3d4048] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] rtl:text-right"
                  >
                    {{ item.brokerShortName || '--' }}
                  </h3>
                  <p
                    v-if="item.tags.length === 1"
                    class="mt-[2px] line-clamp-2 text-[12px] leading-[18px] font-[400] break-words text-[#7d818a]"
                    :style="{ color: item.tags[0]?.textColor || undefined }"
                  >
                    {{ item.tags[0]?.text || '--' }}
                  </p>
                  <p
                    v-else-if="item.tags.length > 1"
                    class="mt-[2px] flex overflow-hidden text-[12px] leading-[18px] font-[400] whitespace-nowrap text-[#7d818a]"
                  >
                    <template v-for="(tag, tagIndex) in item.tags" :key="`${tag.text}-${tagIndex}`">
                      <span class="shrink-0" :style="{ color: tag.textColor || undefined }">{{ tag.text }}</span>
                      <span
                        v-if="tagIndex < item.tags.length - 1"
                        aria-hidden="true"
                        class="mx-[8px] h-[14px] w-[1px] shrink-0 bg-[rgba(11,18,40,0.14)]"
                      ></span>
                    </template>
                  </p>
                </div>
              </div>
              <p class="truncate px-[12px] text-[14px] leading-[20px] font-[500] text-[#292a2e]">
                {{ item.businessTypes || '--' }}
              </p>
              <p class="text-right text-[15px] leading-[22px] font-[700] text-[#3d4048]">☆ {{ item.score || '--' }}</p>
            </NuxtLink>
          </section>

          <section
            v-if="regulatorLayout.hasBlacklistColumn && regulatorDetail?.blacklistList.length"
            :class="{ 'mt-[44px]': regulatorLayout.hasDisclosureColumn || regulatorLayout.hasMemberColumn }"
          >
            <div class="mb-[20px] flex items-center justify-between">
              <h2 class="text-[20px] leading-[28px] font-[700] text-[#34373d]">
                {{ t('84640') }}{{ displayCount(regulatorDetail?.blacklistCount) }}
              </h2>
              <NuxtLink
                v-if="hasMoreBlacklistEntries"
                :to="localePath({ name: 'disclosures-blacklist' })"
                @click="
                  setBreadcrumb('disclosures-blacklist', {
                    label: regulatorTitle, // 走翻译的值
                    route: route.fullPath,
                  })
                "
                target="_blank"
                class="cursor-pointer text-[24px] leading-[24px] text-[#9ca3af] transition-colors hover:text-[#292a2e]"
              >
                ›
              </NuxtLink>
            </div>
            <div
              class="grid grid-cols-[280px_minmax(0,1fr)_80px] border-b border-[#edf0f4] pb-[10px] text-[13px] leading-[18px] text-[#a2a7b0]"
            >
              <span>{{ t('84676') }}</span>
              <span class="px-[12px]">{{ t('84340') }}</span>
              <span class="text-right">{{ t('82495') }}</span>
            </div>
            <NuxtLink
              v-for="item in displayBlacklistItems"
              :key="item.brokerCode || item.brokerShortName"
              :to="getExchangeDetailPath(item.alias)"
              target="_blank"
              class="grid min-h-[72px] cursor-pointer grid-cols-[280px_minmax(0,1fr)_80px] items-center border-b border-[#edf0f4] text-inherit no-underline transition-colors hover:bg-[#f8f8f8]"
            >
              <div class="flex min-w-0 items-center pr-[12px]">
                <div
                  class="flex size-[42px] shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-[#f5f7fb] text-[18px] leading-[24px] font-[800] text-[#142f83]"
                >
                  <CommonImage
                    :src="item.brokerIcon"
                    :alt="item.brokerShortName"
                    class="size-full"
                    object-fit="cover"
                  />
                </div>
                <div class="ml-[13px] min-w-0 rtl:mr-[13px] rtl:ml-0">
                  <h3
                    :dir="isRtl ? 'rtl' : 'ltr'"
                    class="[display:-webkit-box] overflow-hidden text-[16px] leading-[22px] font-[700] break-words text-[#3d4048] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] rtl:text-right"
                  >
                    {{ item.brokerShortName || '--' }}
                  </h3>
                  <p
                    v-if="item.tags.some((tag) => tag.text)"
                    class="mt-[2px] flex items-center text-[12px] leading-[18px] font-[400] text-[#7d818a]"
                  >
                    <template
                      v-for="(tag, tagIndex) in item.tags.filter((tag) => tag.text)"
                      :key="`${tag.text}-${tagIndex}`"
                    >
                      <span :style="{ color: tag.textColor || undefined }">{{ tag.text }}</span>
                      <span
                        v-if="tagIndex < item.tags.filter((tag) => tag.text).length - 1"
                        aria-hidden="true"
                        class="mx-[8px] h-[14px] w-[1px] bg-[rgba(11,18,40,0.14)]"
                      ></span> </template
                    >x
                  </p>
                </div>
              </div>
              <p class="truncate px-[12px] text-[14px] leading-[20px] font-[600] text-[#3f4249]">
                {{ item.businessTypes || '--' }}
              </p>
              <p class="text-right text-[15px] leading-[22px] font-[700] text-[#3d4048]">☆ {{ item.score || '--' }}</p>
            </NuxtLink>
          </section>

          <section v-if="regulatorLayout.shouldFlattenComplaintChannel" class="mt-[44px]">
            <h2 class="mb-[20px] text-[20px] leading-[28px] font-[700] text-[#34373d]">{{ t('84658') }}</h2>
            <div class="grid grid-cols-2 gap-x-[36px]">
              <article
                v-for="item in regulatorDetail?.complaintChannelList || []"
                :key="`${item.type}-${item.text}`"
                class="flex min-h-[92px] cursor-pointer items-start gap-[16px] border-b border-[#edf0f4] py-[20px] transition-colors hover:bg-[#f8f8f8]"
                role="button"
                tabindex="0"
                @click="copyComplaintChannel(item)"
                @keydown.enter="copyComplaintChannel(item)"
                @keydown.space.prevent="copyComplaintChannel(item)"
              >
                <div class="mt-[2px] shrink-0 leading-[22px]">
                  <CommonImage :src="item.icon" :alt="item.text" class="!size-[20px]" object-fit="contain" />
                </div>
                <div class="min-w-0 flex-1">
                  <h3 class="text-[14px] leading-[22px] font-[500] text-[#292A2E]">{{ item.text || '--' }}</h3>
                  <p class="mt-[2px] line-clamp-2 text-[14px] leading-[22px] font-[400] break-all text-[#8C8F97]">
                    {{ item.contactContent || '--' }}
                  </p>
                </div>
              </article>
            </div>
          </section>
        </div>

        <aside class="flex flex-col gap-[18px]">
          <section class="rounded-[8px] border border-[#e3e6eb] bg-[#ffffff] px-[24px] pt-[8px] pb-[24px]">
            <h2 class="border-b border-[#edf0f4] py-[12px] text-[20px] leading-[28px] font-[600] text-[#3b3e46]">
              {{ t('85014') }}
            </h2>
            <dl class="mt-[20px] grid grid-cols-2 gap-[12px]">
              <div class="bg-[#f8f8f8] p-[16px]">
                <dt class="text-[14px] leading-[22px] font-[400] text-[#7d818a]">{{ t('84672') }}</dt>
                <dd class="text-[14px] leading-[22px] font-[500] text-[#292A2E]">
                  {{ regulatorFoundationDate }}
                </dd>
              </div>
              <div class="bg-[#f8f8f8] p-[16px]">
                <dt class="text-[14px] leading-[22px] font-[400] text-[#7d818a]">{{ t('84673') }}</dt>
                <dd class="text-[14px] leading-[22px] font-[500] text-[#292A2E]">
                  {{ regulatorShortName }}
                </dd>
              </div>
              <div class="col-span-2 bg-[#f8f8f8] p-[16px]">
                <dt class="text-[14px] leading-[22px] font-[400] text-[#7d818a]">{{ t('84674') }}</dt>
                <dd class="line-clamp-2 text-[14px] leading-[22px] font-[500] break-all text-[#292A2E]">
                  {{ officialWebsiteText }}
                </dd>
              </div>
            </dl>
            <button
              v-if="officialUrl"
              type="button"
              class="group relative mt-[16px] flex h-[46px] w-full cursor-pointer items-center justify-center overflow-hidden rounded-[999px] bg-[#142f83] text-[14px] leading-[22px] font-[600] text-[#ffffff]"
              @click="openOfficialWebsite"
            >
              <span
                class="pointer-events-none absolute inset-[0] bg-transparent transition-colors group-hover:bg-[rgba(255,255,255,0.08)]"
                aria-hidden="true"
              ></span>
              <span class="relative z-[1]">{{ t('84675') }}</span>
              <ShareOIcon class="relative z-[1] ml-[8px] size-[20px] rtl:mr-[8px] rtl:ml-0" aria-hidden="true" />
            </button>
          </section>

          <RegulatorsLicenseApplication
            v-if="regulatorDetail?.licenseApplicationList.length"
            :items="regulatorDetail?.licenseApplicationList || []"
            :regulator-code="regulatorCode"
            :url-slug="urlSlug"
          />

          <section
            v-if="regulatorDetail?.complaintChannelList.length && !regulatorLayout.shouldFlattenComplaintChannel"
            class="rounded-[8px] border border-[#e3e6eb] bg-[#ffffff] px-[24px] pt-[8px] pb-[24px]"
          >
            <h2 class="border-b border-[#edf0f4] pb-[17px] text-[20px] leading-[28px] font-[700] text-[#3b3e46]">
              {{ t('84658') }}
            </h2>
            <article
              v-for="item in regulatorDetail?.complaintChannelList || []"
              :key="`${item.type}-${item.text}`"
              class="flex cursor-pointer items-center gap-[16px] border-b border-[#edf0f4] py-[20px] transition-colors last:border-b-0 last:pb-0 hover:bg-[#f8f8f8]"
              role="button"
              tabindex="0"
              @click="copyComplaintChannel(item)"
              @keydown.enter="copyComplaintChannel(item)"
              @keydown.space.prevent="copyComplaintChannel(item)"
            >
              <div class="shrink-0 leading-[22px]">
                <CommonImage :src="item.icon" :alt="item.text" class="!size-[20px]" object-fit="contain" />
              </div>
              <div class="min-w-0 flex-1">
                <h3 class="text-[14px] leading-[22px] font-[500] text-[#292A2E]">{{ item.text || '--' }}</h3>
                <p class="mt-[2px] line-clamp-2 text-[14px] leading-[22px] font-[400] break-all text-[#8C8F97]">
                  {{ item.contactContent || '--' }}
                </p>
              </div>
            </article>
          </section>
        </aside>
      </section>
    </div>

    <Teleport to="body">
      <div
        v-if="isRegulationPopupVisible"
        class="fixed inset-[0] z-[1200] flex items-center justify-center bg-[rgba(11,18,40,0.36)] p-[24px]"
        @click.self="closeRegulationPopup"
        @keydown.esc="closeRegulationPopup"
      >
        <section
          class="flex max-h-[min(600px,calc(100vh-48px))] w-[540px] max-w-[calc(100vw-48px)] -translate-y-[80px] flex-col overflow-hidden rounded-[28px] bg-[#ffffff] shadow-[0_24px_80px_rgba(8,19,62,0.18)]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="regulation-popup-title"
        >
          <header
            class="flex items-center justify-between px-[32px] pt-[32px] pb-[16px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)]"
          >
            <h2 id="regulation-popup-title" class="m-[0] text-[20px] leading-[28px] font-[600] text-[#292a2e]">
              {{ t('84671') }}
            </h2>
            <button
              type="button"
              class="size-[32px] cursor-pointer rounded-[999px] transition-opacity hover:opacity-80"
              :aria-label="t('83711')"
              @click="closeRegulationPopup"
            >
              <CloseIcon class="size-full" aria-hidden="true" />
            </button>
          </header>

          <div class="relative min-h-0">
            <div
              ref="regulationPopupBodyRef"
              class="regulation-popup-body max-h-[calc(min(600px,100vh-48px)-80px)] w-full overflow-y-auto px-[32px] pb-[32px]"
              @scroll="syncRegulationPopupScrollMetrics"
            >
              <article
                v-for="(item, index) in regulatorDetail?.regulationNotePopupList || []"
                :key="`${item.infoTitle}-${index}`"
                class="flex gap-[12px] py-[20px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)] last:shadow-none"
              >
                <CommonImage
                  :src="item.infoIcon"
                  :alt="item.infoTitle"
                  class="mt-[2px] !size-[20px] shrink-0"
                  object-fit="contain"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-[12px]">
                    <h3 class="text-[16px] leading-[24px] font-[500] text-[#292a2e]">{{ item.infoTitle || '--' }}</h3>
                    <span class="shrink-0 text-[14px] leading-[22px] font-[400] text-[#8c8f97]">
                      {{ item.rightRequirementText }}
                    </span>
                  </div>
                  <div
                    v-if="item.infoText"
                    class="relative mt-[8px] text-[14px] leading-[22px] font-[400] break-words text-[#8c8f97] [&_p]:m-0"
                  >
                    <div
                      :ref="(element) => setRegulationNoteContentRef(index, element)"
                      :class="{ 'line-clamp-6': !expandedRegulationNoteIndexes.has(index) }"
                    >
                      {{ item.infoText }}
                    </div>
                    <button
                      v-if="expandableRegulationNoteIndexes.has(index) && !expandedRegulationNoteIndexes.has(index)"
                      type="button"
                      class="absolute right-[0px] bottom-[0px] inline-flex size-[20px] cursor-pointer bg-[#ffffff] text-[#7d818a] hover:text-[#7d818a] rtl:right-auto rtl:left-[0px]"
                      aria-label="展开合规要求"
                      @click="expandRegulationNote(index)"
                    >
                      <ArrowDownIcon class="size-[20px] [&_path]:fill-current" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </article>
            </div>
            <div
              v-if="showRegulationPopupScrollbar"
              class="pointer-events-none absolute top-[0px] right-[12px] bottom-[0px] flex w-[4px] items-start rounded-[8px] bg-[rgba(0,0,0,0.05)] rtl:right-auto rtl:left-[12px]"
              aria-hidden="true"
            >
              <div
                class="w-[4px] shrink-0 rounded-[8px] bg-[rgba(0,0,0,0.20)] transition-transform"
                :style="regulationPopupScrollbarThumbStyle"
              ></div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </main>
</template>

<style scoped lang="less">
.regulation-popup-body {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
